import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { API } from '../config';
import { playChime } from '../lib/chime';
import { HELP, LANG_ORDER, PATIENT_LINE, RTL_LANGS, makeT, Flag } from './HelpLinePage';

/**
 * Floating contact stack — 4 icons stacked at bottom-left:
 * Live Chat, WhatsApp, Viber, Messenger.
 *
 * Each opens the appropriate app / modal.
 */

const MESSENGER_URL = 'https://m.me/61590269078912';
const WHATSAPP_NUMBER = '35799931264';
const VIBER_NUMBER = '+35799931264';
const PHONE_OFFICE = '+35722321316';
const PHONE_MOBILE = '+35799931264';
const FACEBOOK_URL = 'https://www.facebook.com/61590269078912';
const INSTAGRAM_URL = 'https://www.instagram.com/j.houseofsecretaries/'; // corrected handle from QR code (plural)
const TIKTOK_URL = 'https://www.tiktok.com/@j.house.of.secret';
const TELEGRAM_URL = 'https://t.me/J_HOUSEOFSECRETARY';
const LINKEDIN_URL = 'https://www.linkedin.com/company/j-house-of-secretary/';
const X_URL = 'https://x.com/JHouseSecretary';
const WEBSITE_URL = 'https://www.jhouseofsecretary.com';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;
const VIBER_URL = `viber://chat?number=${encodeURIComponent(VIBER_NUMBER)}`;

// ------- Device / browser detection for PWA install guidance -------
export const detectPlatform = () => {
  const ua = (typeof navigator !== 'undefined' && (navigator.userAgent || navigator.vendor)) || '';
  const isStandalone =
    (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && navigator.standalone === true);
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  return { isStandalone, isIOS, isAndroid };
};

const ShareGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block align-middle -mt-0.5 text-gold">
    <path d="M12 3v13" /><path d="m8 7 4-4 4 4" /><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7" />
  </svg>
);

// Step-by-step install / notifications guide, adapts to the detected platform.
const InstallGuideModal = ({ type, onClose }) => {
  if (!type) return null;
  const Step = ({ n, children }) => (
    <li className="flex gap-3 items-start">
      <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold text-navy" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }}>{n}</span>
      <span className="text-sm text-slate-200 leading-snug pt-0.5">{children}</span>
    </li>
  );
  let title, body;
  if (type === 'ios') {
    title = 'Εγκατάσταση στο iPhone / iPad';
    body = (
      <ol className="space-y-3" data-testid="install-guide-ios-steps">
        <Step n="1">Πατήστε το κουμπί <b>Κοινή χρήση</b> <ShareGlyph /> στη γραμμή εργαλείων του Safari.<br /><span className="text-slate-400 text-xs">Tap the <b>Share</b> button in Safari.</span></Step>
        <Step n="2">Επιλέξτε <b>«Προσθήκη στην οθόνη Αφετηρίας»</b>.<br /><span className="text-slate-400 text-xs">Choose <b>“Add to Home Screen”</b>.</span></Step>
        <Step n="3">Ανοίξτε την εφαρμογή <b>«Η Γραμμή του Ασθενή»</b> από την οθόνη αφετηρίας.<br /><span className="text-slate-400 text-xs">Open the app from your home screen.</span></Step>
        <Step n="4">Πατήστε ξανά <b>«Ενεργοποίηση ειδοποιήσεων»</b>.<br /><span className="text-slate-400 text-xs">Tap “Enable notifications” again.</span></Step>
      </ol>
    );
  } else if (type === 'android-manual') {
    title = 'Εγκατάσταση στο Android';
    body = (
      <ol className="space-y-3" data-testid="install-guide-android-steps">
        <Step n="1">Πατήστε το μενού <b>⋮</b> πάνω δεξιά στο Chrome.<br /><span className="text-slate-400 text-xs">Open the <b>⋮</b> menu in Chrome.</span></Step>
        <Step n="2">Επιλέξτε <b>«Εγκατάσταση εφαρμογής»</b> ή <b>«Προσθήκη στην αρχική οθόνη»</b>.<br /><span className="text-slate-400 text-xs">Choose <b>“Install app”</b> / “Add to Home screen”.</span></Step>
        <Step n="3">Ανοίξτε την εφαρμογή και πατήστε ξανά για ειδοποιήσεις.<br /><span className="text-slate-400 text-xs">Open the app and enable notifications.</span></Step>
      </ol>
    );
  } else if (type === 'success') {
    title = '✅ Έτοιμο!';
    body = <p className="text-sm text-slate-200 leading-relaxed" data-testid="install-guide-success">Οι ειδοποιήσεις ενεργοποιήθηκαν. Θα λάβετε υπενθύμιση <b>1 μέρα</b> και <b>2 ώρες</b> πριν το ραντεβού σας.</p>;
  } else {
    title = '⚠️ Χρειάζεται άδεια';
    body = <p className="text-sm text-slate-200 leading-relaxed" data-testid="install-guide-denied">Δεν δόθηκε άδεια ειδοποιήσεων. Ενεργοποιήστε τις ειδοποιήσεις για αυτόν τον ιστότοπο από τις ρυθμίσεις του browser και δοκιμάστε ξανά.</p>;
  }
  return (
    <div className="fixed inset-0 z-[10010] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="install-guide-overlay">
      <div className="relative w-full max-w-sm bg-navy border border-gold/40 rounded-2xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-white transition text-lg leading-none" data-testid="install-guide-close" aria-label="Close">✕</button>
        <div className="flex items-center gap-2 mb-4 pr-6">
          <span className="text-2xl">📲</span>
          <h3 className="text-white font-bold text-lg leading-tight">{title}</h3>
        </div>
        {body}
        <button type="button" onClick={onClose} className="mt-5 w-full py-2.5 rounded-lg text-sm font-semibold text-navy transition hover:opacity-90" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }} data-testid="install-guide-ok">Κατάλαβα</button>
      </div>
    </div>
  );
};

// ------- Individual icon buttons -------

const iconBase = 'group relative w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center overflow-hidden shadow-[0_8px_24px_-6px_rgba(0,0,0,0.55)] transition-all duration-300 hover:scale-110 cursor-pointer text-white';

const Tooltip = ({ show, label, align = 'right' }) => (
  align === 'left' ? (
    <div
      className={`hidden md:block absolute right-full mr-3 whitespace-nowrap px-3 py-1.5 rounded-lg bg-[#0a1628] border border-gold/40 shadow-lg text-white text-xs font-medium pointer-events-none transition-all duration-200 ${show ? 'opacity-100' : 'opacity-0'}`}
      style={{ top: '50%', transform: `translateY(-50%) ${show ? 'translateX(0)' : 'translateX(8px)'}` }}
    >
      {label}
      <span className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent border-l-gold/40" />
    </div>
  ) : (
    <div
      className={`hidden md:block absolute left-full ml-3 whitespace-nowrap px-3 py-1.5 rounded-lg bg-[#0a1628] border border-gold/40 shadow-lg text-white text-xs font-medium pointer-events-none transition-all duration-200 ${show ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}
      style={{ top: '50%', transform: `translateY(-50%) ${show ? 'translateX(0)' : 'translateX(-8px)'}` }}
    >
      {label}
      <span className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-transparent border-r-gold/40" />
    </div>
  )
);

const MessengerIcon = () => (
  <svg viewBox="0 0 36 36" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md">
    <path fill="#ffffff" d="M18 2C9.163 2 2 8.646 2 16.85c0 4.673 2.325 8.842 5.955 11.567V34l5.44-2.988c1.452.402 2.987.62 4.605.62 8.837 0 16-6.646 16-14.782C34 8.646 26.837 2 18 2zm1.593 19.9l-4.075-4.345-7.95 4.345 8.75-9.288 4.175 4.345 7.85-4.345L19.593 21.9z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md" fill="#ffffff">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const ViberIcon = () => (
  <img
    src="https://customer-assets.emergentagent.com/job_5ae6b687-efa3-4873-9e8c-8fb765bbdf23/artifacts/h8xr7hq7_VIB.webp"
    alt="Viber"
    className="w-full h-full object-cover scale-[1.35]"
    loading="lazy"
  />
);

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md" fill="none" stroke="#ffffff" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md" fill="#ffffff">
    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21c1.21.48 2.53.74 3.88.74a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.35.26 2.67.74 3.88a1 1 0 01-.21 1.11l-2.41 2.8z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md" fill="#ffffff">
    <path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 011.141.195v3.325a8.623 8.623 0 00-.653-.036 26.805 26.805 0 00-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 00-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md" fill="#ffffff">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md" fill="#ffffff">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md" fill="#ffffff">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md" fill="#ffffff">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 md:w-6 md:h-6 drop-shadow-md" fill="#ffffff">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const WebsiteIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6 md:w-7 md:h-7 drop-shadow-md" fill="none" stroke="#ffffff" strokeWidth={2}>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
  </svg>
);

// ------- Live chat modal (real-time backed by /api/visitor-chat/*) -------

const SESSION_STORAGE_KEY = 'jhouse_visitor_chat_session';

export const LiveChatModal = ({ open, onClose, t, isRTL, allowLangSelect = false }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [chatLang, setChatLang] = useState(null);
  const [message, setMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [error, setError] = useState('');
  const [agentName, setAgentName] = useState('');
  const [typingName, setTypingName] = useState('');
  const [chatStatus, setChatStatus] = useState('open');
  const [chatRating, setChatRating] = useState(null);
  const [ratingHover, setRatingHover] = useState(0);
  const installEvtRef = useRef(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); installEvtRef.current = e; };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);
  const listRef = useRef(null);
  const pollRef = useRef(null);
  const lastTsRef = useRef('');
  const fileBase = API.replace(/\/api$/, '');
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recCancelRef = useRef(false);

  const startRecording = async () => {
    if (!sessionId || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recCancelRef.current = false;
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        clearInterval(recTimerRef.current);
        setRecording(false);
        setRecSecs(0);
        if (recCancelRef.current) return;
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (blob.size < 800) return;
        const ext = (mr.mimeType || '').includes('mp4') ? 'm4a' : (mr.mimeType || '').includes('ogg') ? 'ogg' : 'webm';
        const fd = new FormData();
        fd.append('file', blob, `voice.${ext}`);
        try {
          await axios.post(`${API}/visitor-chat/${sessionId}/voice`, fd);
          lastTsRef.current = '';
        } catch (_e) {
          setError(t('livechat.error') || 'Failed to send. Please try again.');
        }
      };
      mr.start();
      mediaRecRef.current = mr;
      setRecording(true);
      setRecSecs(0);
      recTimerRef.current = setInterval(() => setRecSecs((s) => {
        if (s >= 120) { try { mr.stop(); } catch (_e) {} }
        return s + 1;
      }), 1000);
    } catch (_e) {
      setError(t('livechat.micError') && t('livechat.micError') !== 'livechat.micError' ? t('livechat.micError') : 'Δεν έχουμε πρόσβαση στο μικρόφωνο.');
    }
  };
  const stopRecording = (cancel = false) => {
    recCancelRef.current = cancel;
    try { mediaRecRef.current && mediaRecRef.current.stop(); } catch (_e) {}
  };

  // Restore existing session on open
  useEffect(() => {
    if (!open) { setChatLang(null); return; }
    // A session id in the URL wins over localStorage: it is the only thing that
    // survives the Safari -> installed-PWA hop, which gets its own storage jar.
    let fromUrl = null;
    try { fromUrl = new URLSearchParams(window.location.search).get('s'); } catch (_e) { /* ignore */ }
    const saved = localStorage.getItem(SESSION_STORAGE_KEY);
    if (fromUrl) {
      let base = {};
      try { base = saved ? JSON.parse(saved) : {}; } catch (_e) { base = {}; }
      const merged = { ...base, sessionId: fromUrl };
      try { localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(merged)); } catch (_e) { /* ignore */ }
      setSessionId(fromUrl);
      setName(merged.name || '');
      setPhone(merged.phone || '');
      return;
    }
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSessionId(parsed.sessionId);
        setName(parsed.name || '');
        setPhone(parsed.phone || '');
      } catch { /* ignore */ }
    }
  }, [open]);

  // Fetch messages + start polling once we have a sessionId
  useEffect(() => {
    if (!sessionId || !open) return;
    let cancelled = false;

    const fetchMessages = async (initial = false) => {
      try {
        const url = `${API}/visitor-chat/${sessionId}/messages${initial ? '' : (lastTsRef.current ? `?since=${encodeURIComponent(lastTsRef.current)}` : '')}`;
        const res = await axios.get(url);
        if (cancelled) return;
        const newMsgs = res.data.messages || [];
        setAgentName(res.data.assigned_agent_name || '');
        setTypingName(res.data.agent_typing_name || '');
        if (res.data.status) setChatStatus(res.data.status);
        setChatRating(res.data.rating || null);
        if (initial) {
          setMessages(newMsgs);
        } else if (newMsgs.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const add = newMsgs.filter((m) => m.id && !seen.has(m.id));
            if (add.length && add.some((m) => m.sender !== 'visitor')) playChime();
            return add.length ? [...prev, ...add] : prev;
          });
        }
        if (newMsgs.length) {
          lastTsRef.current = newMsgs[newMsgs.length - 1].created_at;
        }
      } catch (e) {
        if (e.response && e.response.status === 404) {
          // Session gone — reset
          localStorage.removeItem(SESSION_STORAGE_KEY);
          setSessionId(null);
        }
      }
    };

    fetchMessages(true);
    pollRef.current = setInterval(() => fetchMessages(false), 3500);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [sessionId, open]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const handleStart = async () => {
    if (!name.trim() || !message.trim()) return;
    setSending(true);
    setError('');
    try {
      const res = await axios.post(`${API}/visitor-chat/start`, {
        name: name.trim(),
        phone: phone.trim(),
        message: message.trim(),
        lang: chatLang ? HELP[chatLang].en : undefined,
      });
      const sid = res.data.session_id;
      setSessionId(sid);
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ sessionId: sid, name: name.trim(), phone: phone.trim() }));
      setMessage('');
    } catch (e) {
      setError(t('livechat.error') || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleFollowUpSend = async () => {
    if (!followUp.trim() || !sessionId) return;
    const text = followUp.trim();
    setFollowUp('');
    const tmpId = `tmp-${Date.now()}`;
    // Optimistic add
    setMessages((prev) => [...prev, {
      id: tmpId,
      sender: 'visitor',
      text,
      created_at: new Date().toISOString(),
      pending: true,
    }]);
    try {
      await axios.post(`${API}/visitor-chat/${sessionId}/send`, { text });
      // Remove optimistic entry — the next poll will bring the real one from server
      setMessages((prev) => prev.filter((m) => m.id !== tmpId));
    } catch (e) {
      setError(t('livechat.error') || 'Failed to send. Please try again.');
      setMessages((prev) => prev.filter((m) => m.id !== tmpId));
    }
  };

  const handleEndChat = () => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setSessionId(null);
    setMessages([]);
    setName('');
    setPhone('');
    setMessage('');
    lastTsRef.current = '';
    onClose();
  };

  const startNewChat = () => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setSessionId(null);
    setMessages([]);
    setMessage('');
    setChatStatus('open');
    setChatRating(null);
    setAgentName('');
    setTypingName('');
    lastTsRef.current = '';
  };

  const rateService = async (stars) => {
    if (!sessionId) return;
    setChatRating(stars);
    try { await axios.post(`${API}/visitor-chat/${sessionId}/rate`, { rating: stars }); } catch (_e) {}
  };

  const confirmAppointment = async (msg, confirm) => {
    if (!sessionId) return;
    setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, appointment_confirmation: confirm ? 'confirmed' : 'declined' } : m));
    try { await axios.post(`${API}/visitor-chat/${sessionId}/appointment/${msg.appointment_visit_id}/confirm`, { confirm }); } catch (_e) {}
  };

  const [installMsg, setInstallMsg] = useState('');
  const [installModal, setInstallModal] = useState(null);
  const [notifEnabled, setNotifEnabled] = useState(() => {
    try {
      return localStorage.getItem('pl_notif_enabled') === '1' ||
        (typeof Notification !== 'undefined' && Notification.permission === 'granted');
    } catch (_e) { return false; }
  });
  const subscribeVisitorPush = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return false;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return false;
      const reg = await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const key = data.publicKey;
      if (!key) return false;
      const b64 = (key + '='.repeat((4 - key.length % 4) % 4)).replace(/-/g, '+').replace(/_/g, '/');
      const raw = atob(b64);
      const arr = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr });
      const json = sub.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        user_id: sessionId,
        role: 'visitor',
        subscription: { endpoint: json.endpoint, keys: json.keys },
      });
      try { localStorage.setItem('pl_notif_enabled', '1'); } catch (_e) {}
      setNotifEnabled(true);
      return true;
    } catch (_e) { return false; }
  };

  const installApp = async () => {
    setInstallMsg('');
    const { isStandalone, isIOS, isAndroid } = detectPlatform();

    // Already installed / running as an app → just enable notifications.
    if (isStandalone) {
      const ok = await subscribeVisitorPush();
      setInstallModal(ok ? 'success' : 'denied');
      return;
    }

    // iOS (Safari has no install prompt & push needs the app on the home screen first).
    if (isIOS) {
      setInstallModal('ios');
      return;
    }

    // Android Chrome with a native install prompt available → direct install.
    if (installEvtRef.current) {
      installEvtRef.current.prompt();
      try { await installEvtRef.current.userChoice; } catch (_e) {}
      installEvtRef.current = null;
      const ok = await subscribeVisitorPush();
      setInstallModal(ok ? 'success' : (isAndroid ? 'android-manual' : 'denied'));
      return;
    }

    // Fallback (Android without prompt yet, or desktop) → try push, else show manual steps.
    const ok = await subscribeVisitorPush();
    setInstallModal(ok ? 'success' : (isAndroid ? 'android-manual' : 'denied'));
  };

  if (!open) return null;

  // When language selection is enabled (website), use the chosen language's strings.
  if (allowLangSelect && chatLang) {
    t = makeT(HELP[chatLang]);
    isRTL = RTL_LANGS.includes(chatLang);
  }
  const needLang = allowLangSelect && !chatLang && !sessionId;

  const inSession = !!sessionId;

  return (
    <>
    <div
      className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="livechat-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-navy border border-gold/40 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: '85vh' }}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-5 md:p-6 border-b border-slate-700/50">
          <div className="w-10 h-10 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center flex-shrink-0">
            <ChatIcon />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-lg leading-none">{t('livechat.title') || 'Live Chat'}</h3>
            <p className="text-slate-400 text-xs mt-1 truncate">
              {inSession
                ? (t('livechat.connected') || 'Connected — we\'ll reply shortly.')
                : (t('livechat.subtitle') || 'Send us a message — we respond quickly.')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/70 hover:text-white transition flex-shrink-0"
            aria-label="Close"
            data-testid="livechat-close-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        {needLang ? (
          /* Language selection (website live chat) */
          <div className="p-5 md:p-6" data-testid="livechat-lang-select">
            <p className="text-center text-gold/90 text-base font-semibold mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Επιλέξτε τη γλώσσα σας · Select your language
            </p>
            <div className="grid grid-cols-3 gap-2.5 max-h-[52vh] overflow-y-auto pr-1">
              {LANG_ORDER.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setChatLang(code)}
                  className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl transition-all duration-200 hover:scale-105"
                  style={{ background: 'linear-gradient(180deg, rgba(20,34,58,0.85), rgba(12,22,40,0.85))', border: '1.5px solid rgba(201,168,76,0.4)' }}
                  data-testid={`livechat-lang-${code}`}
                >
                  <Flag code={code} small />
                  <span className="text-white font-semibold text-xs leading-tight text-center">{HELP[code].native}</span>
                </button>
              ))}
            </div>
          </div>
        ) : !inSession ? (
          /* Intake form with auto-greeting bubble */
          <div className="p-5 md:p-6 space-y-3">
            {allowLangSelect && chatLang && (
              <button
                type="button"
                onClick={() => setChatLang(null)}
                className="inline-flex items-center gap-2 text-gold/70 hover:text-gold text-xs transition-colors mb-1"
                data-testid="livechat-lang-back"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                <Flag code={chatLang} small />
              </button>
            )}
            <div className="flex justify-start mb-2">
              <div className="max-w-[85%] bg-navy-100 border border-slate-700/60 text-white px-3.5 py-2 rounded-2xl rounded-bl-sm text-sm leading-relaxed">
                {t('livechat.greeting') || 'Hello! How can we help you today?'}
              </div>
            </div>
            <input
              type="text"
              placeholder={t('livechat.name') || 'Name'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-navy-100 border border-slate-700 focus:border-gold text-white text-sm outline-none transition"
              data-testid="livechat-name-input"
            />
            <input
              type="tel"
              placeholder={t('livechat.phone') || 'Phone'}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-navy-100 border border-slate-700 focus:border-gold text-white text-sm outline-none transition"
              data-testid="livechat-phone-input"
            />
            <textarea
              placeholder={t('livechat.message') || 'Your message...'}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-4 py-2.5 rounded-lg bg-navy-100 border border-slate-700 focus:border-gold text-white text-sm outline-none transition resize-none"
              data-testid="livechat-message-input"
            />
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <button
              type="button"
              onClick={handleStart}
              disabled={!name.trim() || !message.trim() || sending}
              className="w-full py-3 rounded-lg bg-gold hover:bg-gold/90 disabled:opacity-40 disabled:cursor-not-allowed text-navy font-bold text-sm uppercase tracking-wider transition"
              data-testid="livechat-send-btn"
            >
              {sending ? (t('livechat.sending') || 'Sending...') : (t('livechat.send') || 'Send Message')}
            </button>
            <p className="text-slate-500 text-[11px] text-center leading-relaxed">
              {t('livechat.hint') || 'Prefer instant chat? Use Messenger, WhatsApp or Viber icons on the left.'}
            </p>
          </div>
        ) : (
          /* Conversation thread */
          <>
            {agentName && (
              <div className="px-4 py-2 bg-navy-200/50 border-b border-gold/20 flex items-center gap-2" data-testid="livechat-agent-header">
                <span className="w-7 h-7 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs font-bold">{agentName.charAt(0).toUpperCase()}</span>
                <span className="text-slate-300 text-xs">{(t('livechat.servedBy') && t('livechat.servedBy') !== 'livechat.servedBy') ? t('livechat.servedBy') : 'Σας εξυπηρετεί'} <b className="text-gold">{agentName}</b></span>
              </div>
            )}
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto p-4 md:p-5 space-y-3 min-h-[280px]"
              data-testid="livechat-messages"
            >
              {messages.map((m) => (
                m.sender === 'system' ? (
                  <div key={m.id} className="flex justify-center" data-testid="livechat-system-msg">
                    <div className="max-w-[85%] px-3 py-1.5 rounded-full bg-emerald-900/30 border border-emerald-600/40 text-emerald-200 text-xs text-center">
                      {m.text_translated || m.text}
                    </div>
                  </div>
                ) : m.sender === 'appointment' ? (
                  <div key={m.id} className="flex justify-start" data-testid="livechat-appointment-msg">
                    <div className="max-w-[85%] px-3.5 py-3 rounded-2xl rounded-bl-sm bg-navy-100 border border-gold/40 text-white text-sm leading-relaxed">
                      <div className="mb-2">{m.text_translated || m.text}</div>
                      {(!m.appointment_confirmation || m.appointment_confirmation === 'pending') ? (
                        <div className="flex gap-2">
                          <button type="button" onClick={() => confirmAppointment(m, true)} className="flex-1 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition" data-testid={`appt-confirm-${m.id}`}>✓ {m.confirm_label || 'Επιβεβαιώνω'}</button>
                          <button type="button" onClick={() => confirmAppointment(m, false)} className="flex-1 py-1.5 rounded-lg text-xs font-semibold border border-red-400/60 text-red-300 hover:bg-red-900/30 transition" data-testid={`appt-decline-${m.id}`}>✗ {m.decline_label || 'Δεν το επιβεβαιώνω'}</button>
                        </div>
                      ) : m.appointment_confirmation === 'confirmed' ? (
                        <div data-testid={`appt-confirmed-${m.id}`}>
                          <div className="text-emerald-300 text-xs font-semibold mb-2">✓ {m.confirm_label || 'Επιβεβαιώθηκε'}</div>
                          <div className="text-slate-300 text-xs mb-2">{m.install_prompt || 'Θέλετε να κατεβάσετε τη Γραμμή του Ασθενή στο κινητό σας;'}</div>
                          <button type="button" onClick={installApp} className="w-full py-2 rounded-lg text-xs font-semibold text-navy transition hover:opacity-90 flex items-center justify-center gap-1.5" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }} data-testid={`appt-install-${m.id}`}>📲 Ενεργοποίηση ειδοποιήσεων & εγκατάσταση</button>
                          {installMsg && <p className="text-[11px] mt-1.5 text-slate-300" data-testid="appt-install-msg">{installMsg}</p>}
                        </div>
                      ) : (
                        <div className="text-red-300 text-xs font-semibold" data-testid={`appt-declined-${m.id}`}>✗ {m.decline_label || 'Δεν επιβεβαιώθηκε'}</div>
                      )}
                    </div>
                  </div>
                ) : (
                <div
                  key={m.id}
                  className={`flex ${m.sender === 'visitor' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                      m.sender === 'visitor'
                        ? 'bg-gold text-navy rounded-br-sm'
                        : 'bg-navy-100 border border-slate-700/60 text-white rounded-bl-sm'
                    } ${m.pending ? 'opacity-60' : ''}`}
                  >
                    {m.sender !== 'visitor' && m.sender_name && (
                      <div className="text-[9px] font-bold uppercase tracking-wider text-gold/70 mb-0.5">{m.sender_name}</div>
                    )}
                    {(m.msg_type === 'voice' || m.audio_url) ? (
                      <div className="flex items-center gap-2" data-testid="livechat-voice-msg">
                        <span className="text-base">🎤</span>
                        <audio controls preload="none" src={`${fileBase}${m.audio_url}`} className="h-9 max-w-[190px]" />
                      </div>
                    ) : (
                      m.sender === 'visitor' ? m.text : (m.text_translated || m.text)
                    )}
                  </div>
                </div>
                )
              ))}
              {typingName && (
                <div className="flex justify-start" data-testid="livechat-typing">
                  <div className="bg-navy-100 border border-slate-700/60 rounded-2xl px-3.5 py-2 flex items-center gap-1.5">
                    <span className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-gold rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                    <span className="text-slate-400 text-[11px] ml-1">{typingName}…</span>
                  </div>
                </div>
              )}
            </div>
            {/* Closing panel (rating + follow + new chat) when the agent has completed the chat */}
            {chatStatus === 'closed' ? (
              <div className="p-4 border-t border-slate-700/50 space-y-4 text-center" data-testid="livechat-closing-panel">
                {!chatRating ? (
                  <div>
                    <p className="text-slate-200 text-sm mb-2">{(t('livechat.rateService') && t('livechat.rateService') !== 'livechat.rateService') ? t('livechat.rateService') : 'Πώς ήταν η εξυπηρέτηση;'}</p>
                    <div className="flex justify-center gap-1.5" data-testid="livechat-rating">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button key={s} type="button" onMouseEnter={() => setRatingHover(s)} onMouseLeave={() => setRatingHover(0)} onClick={() => rateService(s)} className="text-3xl leading-none transition-transform hover:scale-110" data-testid={`livechat-star-${s}`} aria-label={`${s} stars`}>
                          <span style={{ color: (ratingHover || 0) >= s ? '#f5c518' : '#3f4b5f' }}>★</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-gold text-sm font-semibold" data-testid="livechat-rating-thanks">{'★'.repeat(chatRating)}<span className="text-slate-500">{'★'.repeat(5 - chatRating)}</span><br />{(t('livechat.ratingThanks') && t('livechat.ratingThanks') !== 'livechat.ratingThanks') ? t('livechat.ratingThanks') : 'Ευχαριστούμε για τη βαθμολογία σας!'}</p>
                )}

                <div>
                  <p className="text-slate-400 text-xs mb-2">{(t('livechat.followUs') && t('livechat.followUs') !== 'livechat.followUs') ? t('livechat.followUs') : 'Ακολουθήστε μας'}</p>
                  <div className="flex justify-center gap-3" data-testid="livechat-socials">
                    <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="livechat-social-facebook" className="w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-110" style={{ background: '#1877f2' }}><FacebookIcon /></a>
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="livechat-social-instagram" className="w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-110" style={{ background: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)' }}><InstagramIcon /></a>
                    <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" data-testid="livechat-social-tiktok" className="w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-110" style={{ background: '#010101' }}><TikTokIcon /></a>
                    <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="livechat-social-telegram" className="w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-110" style={{ background: '#229ED9' }}><TelegramIcon /></a>
                  </div>
                </div>

                <button type="button" onClick={startNewChat} data-testid="livechat-new-chat-btn" className="w-full py-2.5 rounded-full font-semibold text-navy text-sm transition hover:opacity-90" style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)' }}>
                  ＋ {(t('livechat.newChat') && t('livechat.newChat') !== 'livechat.newChat') ? t('livechat.newChat') : 'Νέα συζήτηση'}
                </button>
              </div>
            ) : (
              <>
            {/* Discreet, persistent notifications CTA — hides once enabled so it never nags */}
            {!notifEnabled && (
              <button
                type="button"
                onClick={installApp}
                className="w-full px-4 py-2 border-t border-gold/15 bg-gold/[0.06] hover:bg-gold/10 transition-colors text-left flex items-center gap-2 group"
                data-testid="livechat-notif-cta"
              >
                <svg className="w-4 h-4 text-gold flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span className="text-[11px] leading-snug text-slate-300">
                  {(t('livechat.notifCtaText') && t('livechat.notifCtaText') !== 'livechat.notifCtaText') ? t('livechat.notifCtaText') : 'Δεν μπορείτε να περιμένετε στη ζωντανή συνομιλία;'}{' '}
                  <span className="text-gold font-semibold underline underline-offset-2 decoration-gold/50 group-hover:decoration-gold" data-testid="livechat-notif-cta-link">
                    {(t('livechat.notifCtaLink') && t('livechat.notifCtaLink') !== 'livechat.notifCtaLink') ? t('livechat.notifCtaLink') : 'Κατεβάστε την στο κινητό σας — πατήστε εδώ'}
                  </span>
                </span>
              </button>
            )}
            {/* Composer */}
            <div className="p-3 md:p-4 border-t border-slate-700/50 flex items-center gap-2">
              {recording ? (
                <div className="flex-1 flex items-center gap-2" data-testid="livechat-recording-bar">
                  <button type="button" onClick={() => stopRecording(true)} className="w-9 h-9 rounded-full bg-navy-100 border border-slate-600 text-slate-300 hover:text-white flex items-center justify-center transition" data-testid="livechat-rec-cancel" aria-label="Cancel">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" /></svg>
                  </button>
                  <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-full bg-red-950/40 border border-red-500/50">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-red-200 text-sm font-mono">{String(Math.floor(recSecs / 60)).padStart(1, '0')}:{String(recSecs % 60).padStart(2, '0')}</span>
                    <span className="text-red-300/70 text-xs ml-auto">● REC</span>
                  </div>
                  <button type="button" onClick={() => stopRecording(false)} className="w-10 h-10 rounded-full bg-gold hover:bg-gold/90 text-navy flex items-center justify-center transition" data-testid="livechat-rec-send" aria-label="Send voice">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={followUp}
                    onChange={(e) => setFollowUp(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleFollowUpSend(); }}
                    placeholder={t('livechat.placeholder') || 'Type a message...'}
                    className="flex-1 px-4 py-2.5 rounded-full bg-navy-100 border border-slate-700 focus:border-gold text-white text-sm outline-none transition"
                    data-testid="livechat-composer-input"
                  />
                  {followUp.trim() ? (
                    <button
                      type="button"
                      onClick={handleFollowUpSend}
                      className="w-10 h-10 rounded-full bg-gold hover:bg-gold/90 text-navy flex items-center justify-center transition"
                      data-testid="livechat-composer-send"
                      aria-label="Send"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={startRecording}
                      className="w-10 h-10 rounded-full bg-gold hover:bg-gold/90 text-navy flex items-center justify-center transition"
                      data-testid="livechat-composer-mic"
                      aria-label="Record voice message"
                      title="Φωνητικό μήνυμα"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 14a3 3 0 003-3V5a3 3 0 00-6 0v6a3 3 0 003 3z" /><path d="M19 11a7 7 0 01-14 0H3a9 9 0 008 8.94V23h2v-3.06A9 9 0 0021 11h-2z" /></svg>
                    </button>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              onClick={handleEndChat}
              className="text-[11px] text-slate-500 hover:text-white transition py-2 border-t border-slate-800/50"
              data-testid="livechat-end-btn"
            >
              {t('livechat.end') || 'End chat'}
            </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
    <InstallGuideModal type={installModal} onClose={() => setInstallModal(null)} />
    </>
  );
};

// ------- QR modal (elegant navy/gold luxury style) -------

// Gold on navy QR for premium look
const buildQrUrl = (data) => `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&color=c9a84c&bgcolor=0a1628&format=png&data=${encodeURIComponent(data)}`;


const OrnamentDivider = () => (
  <svg viewBox="0 0 200 12" className="w-24 h-3 opacity-70" fill="none">
    <path d="M2 6 L60 6 M140 6 L198 6" stroke="#c9a84c" strokeWidth="1" strokeLinecap="round" />
    <path d="M60 6 Q75 -2 90 6 Q100 12 110 6 Q125 -2 140 6" stroke="#c9a84c" strokeWidth="1.2" fill="none" />
    <circle cx="100" cy="6" r="1.5" fill="#c9a84c" />
  </svg>
);

const SocialQrModal = ({ open, onClose, platform, url, handle, iconTint, brandCircleBg, brandCircleIcon, qrImage, t }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="social-qr-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #0a1628 0%, #0d1a30 100%)',
          border: '1px solid rgba(201,168,76,0.5)',
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.8), 0 0 40px -10px rgba(201,168,76,0.15), inset 0 1px 0 rgba(201,168,76,0.2)',
        }}
        data-testid={`social-qr-modal-${platform.toLowerCase()}`}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gold/10 flex items-center justify-center text-gold transition z-10"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Top: J. House Logo */}
        <div className="pt-6 pb-2 flex justify-center">
          <img
            src="/brand-logo-header.png"
            alt="J. House of Secretary"
            className="h-16 w-auto object-contain rounded-md"
            data-testid="social-qr-modal-brand-logo"
          />
        </div>

        {/* Platform title */}
        <div className="px-6 pb-4 flex items-center justify-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: brandCircleBg }}
          >
            {brandCircleIcon}
          </div>
          <div className="text-left">
            <p className="text-gold/80 text-[10px] uppercase tracking-[0.25em] font-semibold leading-tight">{t('livechat.followUs') || 'ΑΚΟΛΟΥΘΗΣΤΕ'}</p>
            <p className="text-gold font-serif text-2xl italic leading-tight" style={{ fontFamily: 'Georgia, serif' }}>{platform}</p>
          </div>
        </div>

        {/* Gold divider line with dot */}
        <div className="flex items-center justify-center px-8 pb-4">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/60 to-gold/80" />
          <span className="w-1 h-1 rounded-full bg-gold mx-1 shadow-[0_0_8px_#c9a84c]" />
          <span className="h-px flex-1 bg-gradient-to-l from-transparent via-gold/60 to-gold/80" />
        </div>

        {/* QR code with gold border */}
        <div className="px-6 pb-5 flex justify-center">
          <div
            className="p-3 rounded-2xl"
            style={{
              background: '#0a1628',
              border: '2px solid #c9a84c',
              boxShadow: '0 0 30px -8px rgba(201,168,76,0.5), inset 0 0 20px -10px rgba(201,168,76,0.3)',
            }}
          >
            <img
              src={qrImage || buildQrUrl(url)}
              alt={`${platform} QR code`}
              className={qrImage ? 'w-56 md:w-60 h-auto block' : 'w-56 h-56 md:w-60 md:h-60 block'}
              loading="lazy"
            />
          </div>
        </div>

        {/* Instruction (Greek serif) */}
        <p
          className="text-gold/90 text-sm text-center leading-relaxed px-8 pb-4"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic' }}
        >
          {t('livechat.qrHint') || 'Σαρώστε με την κάμερα του κινητού σας για να ανοίξει η σελίδα μας'}
        </p>

        {/* Ornament divider */}
        <div className="flex justify-center pb-3">
          <OrnamentDivider />
        </div>

        {/* Handle chip */}
        <div className="px-6 pb-3">
          <div
            className="rounded-lg py-2.5 px-4 text-center text-gold font-mono text-sm tracking-wider"
            style={{ border: '1px solid rgba(201,168,76,0.5)', background: 'rgba(201,168,76,0.05)' }}
            data-testid={`social-qr-handle-${platform.toLowerCase()}`}
          >
            {handle}
          </div>
        </div>

        {/* Open Page button (gold gradient) */}
        <div className="px-6 pb-6">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="block w-full py-3 rounded-lg text-center font-semibold text-sm uppercase tracking-[0.15em] transition hover:shadow-[0_10px_30px_-5px_rgba(201,168,76,0.6)]"
            style={{
              background: 'linear-gradient(180deg, #d4b661 0%, #c9a84c 50%, #a17f2f 100%)',
              color: '#0a1628',
              border: '1px solid rgba(201,168,76,0.9)',
              boxShadow: '0 4px 15px -3px rgba(201,168,76,0.4), inset 0 1px 0 rgba(255,255,255,0.4)',
              fontFamily: 'Georgia, serif',
            }}
            data-testid={`social-qr-open-${platform.toLowerCase()}`}
          >
            {t('livechat.openPage') || 'ΑΝΟΙΓΜΑ ΣΕΛΙΔΑΣ'} →
          </a>
        </div>
      </div>
    </div>
  );
};

// ------- Main stack -------

export const FloatingContactStack = () => {
  const { t, isRTL } = useLanguage();
  const [hoveredKey, setHoveredKey] = useState(null);
  const [visible, setVisible] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [phoneMenuOpen, setPhoneMenuOpen] = useState(false);
  const [qrPlatform, setQrPlatform] = useState(null); // 'facebook' | 'instagram' | null

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Reopen the chat when arriving from a push notification (URL param or SW message).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('openchat') === '1') {
        setChatOpen(true);
        params.delete('openchat');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      }
    } catch (_e) {}
    const onSwMsg = (event) => {
      if (event.data && event.data.type === 'OPEN_CHAT') setChatOpen(true);
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMsg);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMsg);
      }
    };
  }, []);

  // Close phone menu when clicking outside
  useEffect(() => {
    if (!phoneMenuOpen) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="phone-popover"]') && !e.target.closest('[data-testid="contact-phone-btn"]')) {
        setPhoneMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [phoneMenuOpen]);

  const contactItems = [
    {
      key: 'patientline',
      label: t('livechat.patientLine') || 'Η Γραμμή του Ασθενή',
      style: { background: 'transparent', padding: 0 },
      onClick: () => { window.location.href = '/help'; },
      icon: <img src="/patient-line-icon.png" alt="Patient Line" className="w-full h-full object-cover rounded-full" draggable={false} />,
      testId: 'contact-patientline-btn',
      pulse: true,
    },
    {
      key: 'livechat',
      label: t('livechat.tooltip') || 'Live Chat',
      style: { background: 'linear-gradient(135deg, #c9a84c 0%, #d4b661 100%)' },
      onClick: () => { setPhoneMenuOpen(false); setChatOpen(true); },
      icon: <ChatIcon />,
      testId: 'contact-livechat-btn',
    },
    {
      key: 'phone',
      label: t('livechat.callUs') || 'Call Us',
      style: { background: 'linear-gradient(135deg, #0a1628 0%, #1a2942 100%)', border: '1px solid rgba(201,168,76,0.6)' },
      onClick: () => setPhoneMenuOpen((v) => !v),
      icon: <PhoneIcon />,
      testId: 'contact-phone-btn',
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      style: { background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' },
      onClick: () => window.open(`https://wa.me/${WHATSAPP_NUMBER}`, '_blank', 'noopener,noreferrer'),
      icon: <WhatsAppIcon />,
      testId: 'contact-whatsapp-btn',
    },
    {
      key: 'viber',
      label: 'Viber',
      style: { background: 'transparent', padding: 0 },
      onClick: () => window.open(`viber://chat?number=${encodeURIComponent(VIBER_NUMBER)}`, '_blank', 'noopener,noreferrer'),
      icon: <ViberIcon />,
      testId: 'contact-viber-btn',
    },
    {
      key: 'messenger',
      label: 'Messenger',
      style: { background: 'linear-gradient(135deg, #00c6ff 0%, #0084ff 40%, #a033ff 100%)' },
      onClick: () => window.open(MESSENGER_URL, '_blank', 'noopener,noreferrer'),
      icon: <MessengerIcon />,
      testId: 'contact-messenger-btn',
    },
  ];

  const agentLoginItem = {
    key: 'agentlogin',
    label: 'Σύνδεση Προσωπικού',
    style: { background: 'transparent', padding: 0, border: '1px solid rgba(201,168,76,0.6)' },
    onClick: () => { window.location.href = '/agent'; },
    icon: <img src="/agent-icon.png" alt="Agent Login" className="w-full h-full object-cover rounded-full" draggable={false} />,
    testId: 'contact-agent-login-btn',
  };

  const socialItems = [
    {
      key: 'facebook',
      label: 'Facebook',
      style: { background: 'linear-gradient(135deg, #1877F2 0%, #0d5cbf 100%)' },
      onClick: () => setQrPlatform('facebook'),
      icon: <FacebookIcon />,
      testId: 'contact-facebook-btn',
    },
    {
      key: 'instagram',
      label: 'Instagram',
      style: { background: 'linear-gradient(135deg, #F58529 0%, #DD2A7B 45%, #8134AF 75%, #515BD4 100%)' },
      onClick: () => setQrPlatform('instagram'),
      icon: <InstagramIcon />,
      testId: 'contact-instagram-btn',
    },
    {
      key: 'tiktok',
      label: 'TikTok',
      style: { background: 'linear-gradient(135deg, #010101 0%, #25F4EE 50%, #FE2C55 100%)' },
      onClick: () => setQrPlatform('tiktok'),
      icon: <TikTokIcon />,
      testId: 'contact-tiktok-btn',
    },
    {
      key: 'telegram',
      label: 'Telegram',
      style: { background: 'linear-gradient(135deg, #37bbfe 0%, #007dbb 100%)' },
      onClick: () => setQrPlatform('telegram'),
      icon: <TelegramIcon />,
      testId: 'contact-telegram-btn',
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      style: { background: 'linear-gradient(135deg, #0A66C2 0%, #004182 100%)' },
      onClick: () => setQrPlatform('linkedin'),
      icon: <LinkedInIcon />,
      testId: 'contact-linkedin-btn',
    },
    {
      key: 'x',
      label: 'X',
      style: { background: '#000000', boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,0.35)' },
      onClick: () => setQrPlatform('x'),
      icon: <XIcon />,
      testId: 'contact-x-btn',
    },
  ];

  const renderItem = (item, idx, align) => (
    <div
      key={item.key}
      className="relative"
      style={{ animation: visible ? `contact-slide-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${idx * 0.08}s both` : 'none' }}
    >
      <Tooltip show={hoveredKey === item.key && !(item.key === 'phone' && phoneMenuOpen)} label={item.label} align={align} />

      {item.key === 'phone' && phoneMenuOpen && (
        <div
          data-testid="phone-popover"
          className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-10 bg-[#0a1628] border border-gold/50 rounded-xl shadow-2xl overflow-hidden min-w-[220px]"
          style={{ animation: 'contact-slide-in 0.25s ease-out both' }}
        >
          <a
            href={`tel:${PHONE_OFFICE}`}
            onClick={() => setPhoneMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 hover:bg-gold/10 transition border-b border-slate-700/50"
            data-testid="phone-office-link"
          >
            <div className="w-8 h-8 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider text-gold/80 font-semibold">{t('livechat.office') || 'Office'}</span>
              <span className="text-white text-sm font-medium">+357 22321316</span>
            </div>
          </a>
          <a
            href={`tel:${PHONE_MOBILE}`}
            onClick={() => setPhoneMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 hover:bg-gold/10 transition"
            data-testid="phone-mobile-link"
          >
            <div className="w-8 h-8 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider text-gold/80 font-semibold">{t('livechat.mobile') || 'Mobile'}</span>
              <span className="text-white text-sm font-medium">+357 99931264</span>
            </div>
          </a>
        </div>
      )}

      <button
        type="button"
        onClick={item.onClick}
        onMouseEnter={() => setHoveredKey(item.key)}
        onMouseLeave={() => setHoveredKey(null)}
        className={iconBase}
        style={item.style}
        aria-label={item.label}
        data-testid={item.testId}
      >
        {item.icon}
        {item.pulse && (
          <span
            aria-hidden
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{ animation: 'contact-pulse-gold 2.4s ease-out infinite' }}
          />
        )}
      </button>
    </div>
  );

  return (
    <>
      {/* Staff login: pinned bottom-left, very faint & small so visitors barely notice it.
          Fades to full opacity on hover/tap for staff who need it. */}
      <div
        className="fixed bottom-2 left-2 md:bottom-3 md:left-4 z-[9998] opacity-[0.12] hover:opacity-100 focus-within:opacity-100 active:opacity-100 transition-opacity duration-300 scale-[0.6] md:scale-[0.65] origin-bottom-left"
        data-testid="floating-staff-login"
      >
        {renderItem(agentLoginItem, 0, 'right')}
      </div>

      {/* Left stack: contact / communication */}
      <div
        className={`fixed bottom-16 left-4 md:bottom-20 md:left-6 z-[9997] flex flex-col gap-3 transition-opacity duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
        data-testid="floating-contact-stack"
      >
        {contactItems.map((item, idx) => renderItem(item, idx, 'right'))}
      </div>

      {/* Right stack: follow us / social */}
      <div
        className={`fixed bottom-16 right-4 md:bottom-20 md:right-6 z-[9997] flex flex-col items-center gap-3 transition-opacity duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
        data-testid="floating-social-stack"
      >
        <span className="text-[9px] uppercase tracking-[0.22em] text-gold/60 font-semibold" aria-hidden>
          {t('livechat.followUs') || 'Follow'}
        </span>
        {socialItems.map((item, idx) => renderItem(item, idx, 'left'))}
      </div>

      <LiveChatModal open={chatOpen} onClose={() => setChatOpen(false)} t={t} isRTL={isRTL} allowLangSelect />

      <SocialQrModal
        open={qrPlatform === 'facebook'}
        onClose={() => setQrPlatform(null)}
        platform="Facebook"
        url={FACEBOOK_URL}
        handle="@J.HOUSEOFSECRETARY"
        qrImage="/facebook-qr-gold-v2.png"
        brandCircleBg="linear-gradient(135deg, #1877F2 0%, #0d5cbf 100%)"
        brandCircleIcon={<FacebookIcon />}
        t={t}
      />
      <SocialQrModal
        open={qrPlatform === 'instagram'}
        onClose={() => setQrPlatform(null)}
        platform="Instagram"
        url={INSTAGRAM_URL}
        handle="@J.HOUSEOFSECRETARIES"
        qrImage="/instagram-qr-gold-v1.png"
        brandCircleBg="linear-gradient(135deg, #F58529 0%, #DD2A7B 45%, #8134AF 75%, #515BD4 100%)"
        brandCircleIcon={<InstagramIcon />}
        t={t}
      />
      <SocialQrModal
        open={qrPlatform === 'tiktok'}
        onClose={() => setQrPlatform(null)}
        platform="TikTok"
        url={TIKTOK_URL}
        handle="@J.HOUSE.OF.SECRET"
        qrImage="/tiktok-qr-gold-v1.png"
        brandCircleBg="linear-gradient(135deg, #010101 0%, #25F4EE 50%, #FE2C55 100%)"
        brandCircleIcon={<TikTokIcon />}
        t={t}
      />
      <SocialQrModal
        open={qrPlatform === 'telegram'}
        onClose={() => setQrPlatform(null)}
        platform="Telegram"
        url={TELEGRAM_URL}
        handle="@J_HOUSEOFSECRETARY"
        qrImage="/telegram-qr-gold-v1.png"
        brandCircleBg="linear-gradient(135deg, #37bbfe 0%, #007dbb 100%)"
        brandCircleIcon={<TelegramIcon />}
        t={t}
      />
      <SocialQrModal
        open={qrPlatform === 'linkedin'}
        onClose={() => setQrPlatform(null)}
        platform="LinkedIn"
        url={LINKEDIN_URL}
        handle="J.HOUSE OF SECRETARY"
        qrImage="/linkedin-qr-gold-v1.png"
        brandCircleBg="linear-gradient(135deg, #0A66C2 0%, #004182 100%)"
        brandCircleIcon={<LinkedInIcon />}
        t={t}
      />
      <SocialQrModal
        open={qrPlatform === 'x'}
        onClose={() => setQrPlatform(null)}
        platform="X"
        url={X_URL}
        handle="@JHouseSecretary"
        qrImage="/x-qr-gold-v1.png"
        brandCircleBg="#000000"
        brandCircleIcon={<XIcon />}
        t={t}
      />

      <style>{`
        @keyframes contact-slide-in {
          0% { opacity: 0; transform: translateX(-30px) scale(0.7); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes contact-pulse {
          0% { box-shadow: 0 0 0 0 rgba(0,132,255,0.55); }
          70% { box-shadow: 0 0 0 18px rgba(0,132,255,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,132,255,0); }
        }
        @keyframes contact-pulse-gold {
          0% { box-shadow: 0 0 0 0 rgba(201,168,76,0.6); }
          70% { box-shadow: 0 0 0 16px rgba(201,168,76,0); }
          100% { box-shadow: 0 0 0 0 rgba(201,168,76,0); }
        }
      `}</style>
    </>
  );
};

export default FloatingContactStack;

